import React, { useEffect, useState } from "react";
import { Loader2, Boxes, Download, Check, MessageSquare, Clock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import { Link } from "react-router-dom";
import STLViewer from "@/components/STLViewer";
import usePreloadUrls from "@/hooks/usePreloadUrls";

const uploadUrl = (p) => (p && p.startsWith("http") ? p : `${process.env.REACT_APP_BACKEND_URL}/api/uploads/${p}`);
const isGlb = (p) => (p || "").toLowerCase().endsWith(".glb");
const isStl = (p) => (p || "").toLowerCase().endsWith(".stl");
const is3mf = (p) => (p || "").toLowerCase().endsWith(".3mf");

const Studio = () => {
    const { user } = useAuth();
    const { settings } = useSettings();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null);

    const load = async () => {
        try {
            const { data } = await api.get("/orders/mine");
            setOrders(data.orders || []);
        } catch {}
        setLoading(false);
    };
    useEffect(() => { load(); }, []);

    // Pre-warm the browser cache for every uploaded STL/GLB so the viewer paints instantly.
    const preloadHrefs = React.useMemo(() => {
        const urls = [];
        for (const o of orders) {
            for (const it of (o.items || [])) {
                for (const p of (it.production_files || [])) {
                    if (isStl(p) || isGlb(p) || is3mf(p)) urls.push(uploadUrl(p));
                }
            }
        }
        return urls;
    }, [orders]);
    usePreloadUrls(preloadHrefs);

    const approve = async (orderId, idx) => {
        setBusy(`${orderId}-${idx}`);
        try {
            await api.post(`/orders/mine/${orderId}/items/${idx}/approve`);
            toast.success("Preview approved — we'll begin production!");
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
        finally { setBusy(null); }
    };

    const requestChanges = async (orderId, idx) => {
        const note = window.prompt("Describe the changes you'd like (pose, colour, expression…):");
        if (!note) return;
        setBusy(`${orderId}-${idx}-r`);
        try {
            await api.post(`/orders/mine/${orderId}/items/${idx}/revise`, { note });
            toast.success("Change request sent — our team will get on it!");
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
        finally { setBusy(null); }
    };

    const waLink = (order, item) => {
        const wa = (settings?.contact?.whatsapp || "").replace(/[^0-9]/g, "");
        if (!wa) return null;
        const msg = encodeURIComponent(`Hi Cherishables! About my order ${order.order_number} (${item.product_name}) — I'd like to discuss the preview.`);
        return `https://wa.me/${wa}?text=${msg}`;
    };

    if (!user) {
        return (
            <div className="max-w-3xl mx-auto px-6 py-16 text-center" data-testid="studio-signin-prompt">
                <div className="text-xs uppercase tracking-widest text-brand-gold font-semibold">Cherishables Studio</div>
                <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight mt-1">Peek into production</h1>
                <p className="text-muted-foreground mt-3">Sign in to see live 3D previews of your custom pieces once we've verified your reference photos.</p>
                <div className="mt-6"><Link to="/auth/login" className="rounded-full px-5 h-11 inline-flex items-center bg-primary text-primary-foreground text-sm font-semibold">Sign in</Link></div>
            </div>
        );
    }

    const activeOrders = orders.filter((o) => !["order_received"].includes(o.status));

    return (
        <div className="max-w-5xl mx-auto px-6 py-12" data-testid="studio-page">
            <div className="text-xs uppercase tracking-widest text-brand-gold font-semibold">Cherishables Studio</div>
            <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight mt-1">Your models in production</h1>
            <p className="text-muted-foreground mt-2">Once we verify your reference photos, our team uploads a live 3D preview here. Rotate, zoom and inspect before the final print.</p>

            {loading ? (
                <div className="mt-10 text-center"><Loader2 className="w-8 h-8 mx-auto animate-spin text-primary" /></div>
            ) : activeOrders.length === 0 ? (
                <div className="mt-10 p-8 text-center rounded-3xl glass" data-testid="studio-empty">
                    <Boxes className="w-8 h-8 mx-auto text-muted-foreground opacity-60" />
                    <div className="text-sm text-muted-foreground mt-2">You don't have any orders in production yet. Place one on <Link to="/shop" className="text-primary underline">/shop</Link>!</div>
                </div>
            ) : (
                <div className="mt-8 space-y-8">
                    {activeOrders.map((o) => (
                        <div key={o.id} className="rounded-3xl glass p-5" data-testid={`studio-order-${o.order_number}`}>
                            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                                <div>
                                    <div className="font-display text-lg font-semibold">Order {o.order_number}</div>
                                    <div className="text-xs text-muted-foreground">Status: {(o.status || "").replace(/_/g, " ")}</div>
                                </div>
                                <Link to={`/track?number=${o.order_number}`} className="text-xs text-primary underline">See timeline</Link>
                            </div>
                            <div className="grid md:grid-cols-2 gap-4">
                                {(o.items || []).map((it, idx) => {
                                    const files = it.production_files || [];
                                    const mf = [...files].reverse().find(is3mf);
                                    const glb = !mf ? [...files].reverse().find(isGlb) : null;
                                    const stl = !mf && !glb ? [...files].reverse().find(isStl) : null;
                                    const meshFile = mf || glb || stl;
                                    return (
                                        <div key={idx} className="rounded-2xl bg-white/60 p-3 border" data-testid={`studio-item-${o.order_number}-${idx}`}>
                                            <div className="font-semibold text-sm">{it.product_name}</div>
                                            <div className="text-xs text-muted-foreground">{it.size_label} · Qty {it.quantity}</div>
                                            {meshFile ? (
                                                <div className="mt-3 rounded-xl overflow-hidden bg-gradient-to-br from-brand-cream to-brand-lavender relative" style={{ height: 320 }}>
                                                    <STLViewer
                                                        src={uploadUrl(meshFile)}
                                                        height={320}
                                                        testId={`studio-viewer-${o.order_number}-${idx}`}
                                                    />
                                                    {mf && <div className="absolute top-2 left-2 text-[10px] px-2 py-1 rounded-full bg-white/70 text-brand-gold font-semibold pointer-events-none" data-testid={`studio-3mf-badge-${o.order_number}-${idx}`}>Colour preview (3MF)</div>}
                                                    <div className="absolute bottom-2 right-2 text-[10px] px-2 py-1 rounded-full bg-black/40 text-white pointer-events-none">Drag to rotate · Scroll to zoom</div>
                                                </div>
                                            ) : files.length === 0 ? (
                                                <div className="mt-3 p-4 rounded-xl bg-amber-50 border border-amber-200 text-[11px] text-amber-900" data-testid={`studio-pending-${o.order_number}-${idx}`}>
                                                    Awaiting production preview — our team will upload a 3D model here once your photos are verified.
                                                </div>
                                            ) : null}
                                            {files.length > 0 && (
                                                <div className="mt-4 pt-4 border-t border-border/60 space-y-2" data-testid={`studio-approval-${o.order_number}-${idx}`}>
                                                    {it.approval_status === "approved" ? (
                                                        <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-[11px] text-emerald-800 inline-flex items-center gap-1"><Check className="w-3 h-3" /> You approved this preview on {new Date(it.approved_at || "").toLocaleDateString()}. We're now producing your piece.</div>
                                                    ) : it.approval_status === "revision_requested" ? (
                                                        <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-[11px] text-amber-900" data-testid={`studio-revision-note-${o.order_number}-${idx}`}>
                                                            <div className="inline-flex items-center gap-1 font-semibold"><Clock className="w-3 h-3" /> Changes requested</div>
                                                            <div className="mt-1 italic">"{it.revision_note}"</div>
                                                            <div className="mt-1">Our team will upload a revised preview shortly.</div>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <div className="text-[11px] text-muted-foreground">Happy with this preview? Approve to begin production — or request tweaks.</div>
                                                            <div className="flex flex-wrap gap-2">
                                                                <button
                                                                    onClick={() => approve(o.id, idx)}
                                                                    disabled={busy === `${o.id}-${idx}`}
                                                                    data-testid={`studio-approve-${o.order_number}-${idx}`}
                                                                    className="text-xs px-4 h-9 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center gap-1 disabled:opacity-50"
                                                                >
                                                                    <Check className="w-3 h-3" /> Approve preview
                                                                </button>
                                                                <button
                                                                    onClick={() => requestChanges(o.id, idx)}
                                                                    disabled={busy === `${o.id}-${idx}-r`}
                                                                    data-testid={`studio-request-changes-${o.order_number}-${idx}`}
                                                                    className="text-xs px-4 h-9 rounded-full border border-primary/40 text-primary hover:bg-primary/5 inline-flex items-center gap-1 disabled:opacity-50"
                                                                >
                                                                    <MessageSquare className="w-3 h-3" /> Request changes
                                                                </button>
                                                                {waLink(o, it) && (
                                                                    <a href={waLink(o, it)} target="_blank" rel="noreferrer" data-testid={`studio-whatsapp-${o.order_number}-${idx}`} className="text-xs px-4 h-9 rounded-full border border-emerald-500 text-emerald-700 hover:bg-emerald-50 inline-flex items-center gap-1"><i className="fa-brands fa-whatsapp" /> Chat on WhatsApp</a>
                                                                )}
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default Studio;
